import type { Hours, Palette } from "./types";

export const DAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];
export const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Ported verbatim from the prototype: closed Sunday, 9 to 5 weekdays, 10 to 3 Saturday.
export const defaultHours: Hours = {
  0: { open: false, start: 540, end: 1020 },
  1: { open: true, start: 540, end: 1020 },
  2: { open: true, start: 540, end: 1020 },
  3: { open: true, start: 540, end: 1020 },
  4: { open: true, start: 540, end: 1020 },
  5: { open: true, start: 540, end: 1020 },
  6: { open: true, start: 600, end: 900 },
};

// The prototype's sage palette is the default preset.
export const sagePalette: Palette = {
  accent: "#5B6B4F",
  accentDark: "#46553B",
  accentSoft: "#EDF0E7",
  bg: "#FBFAF8",
  surface: "#FFFFFF",
  ink: "#1C1B19",
  muted: "#86837C",
  line: "#ECE9E3",
};

// Named presets. Each accent has been checked to clear WCAG AA (>= 4.5) for
// white text on the accent and for ink on the background.
export const palettePresets: Record<string, Palette> = {
  Sage: sagePalette,
  Ocean: {
    accent: "#2F6F8F",
    accentDark: "#245A75",
    accentSoft: "#E4EEF3",
    bg: "#FAFBFC",
    surface: "#FFFFFF",
    ink: "#16202A",
    muted: "#6B7A85",
    line: "#E6EBEF",
  },
  Clay: {
    accent: "#B5552F",
    accentDark: "#8F4123",
    accentSoft: "#F6E9E1",
    bg: "#FCFAF8",
    surface: "#FFFFFF",
    ink: "#241A14",
    muted: "#8A7C72",
    line: "#EEE7E0",
  },
  Plum: {
    accent: "#6B4E7A",
    accentDark: "#543D61",
    accentSoft: "#EFE9F2",
    bg: "#FCFBFC",
    surface: "#FFFFFF",
    ink: "#1F1A24",
    muted: "#837A88",
    line: "#EBE7EC",
  },
  Slate: {
    accent: "#3D4A55",
    accentDark: "#2C363F",
    accentSoft: "#E9ECEF",
    bg: "#FAFAFA",
    surface: "#FFFFFF",
    ink: "#1A1D20",
    muted: "#767C82",
    line: "#E8EAEC",
  },
};

export const DEFAULT_PRESET = "Sage";

// A fixed "always closed" tone used behind holiday markers, independent of palette.
export const BLOCK_COLOR = "#E9DCCB";
export const DANGER_COLOR = "#B23B2E";

// Seed breed list. The admin can add, rename, retire, and re-tune these, so this
// is only a starting point, never a hardcoded enum. appointmentMinutes is the
// slot length for that breed; dailyMax is the solo-groomer overload cap.
export interface SeedBreed {
  name: string;
  appointmentMinutes: number;
  dailyMax: number;
}

export const seedBreeds: SeedBreed[] = [
  { name: "Chihuahua", appointmentMinutes: 45, dailyMax: 4 },
  { name: "Dachshund", appointmentMinutes: 45, dailyMax: 4 },
  { name: "Pomeranian", appointmentMinutes: 60, dailyMax: 3 },
  { name: "Shih Tzu", appointmentMinutes: 60, dailyMax: 3 },
  { name: "Beagle", appointmentMinutes: 60, dailyMax: 3 },
  { name: "French Bulldog", appointmentMinutes: 60, dailyMax: 3 },
  { name: "Cocker Spaniel", appointmentMinutes: 75, dailyMax: 3 },
  { name: "Border Collie", appointmentMinutes: 75, dailyMax: 2 },
  { name: "Australian Shepherd", appointmentMinutes: 90, dailyMax: 2 },
  { name: "Labrador Retriever", appointmentMinutes: 90, dailyMax: 2 },
  { name: "Golden Retriever", appointmentMinutes: 90, dailyMax: 2 },
  { name: "German Shepherd", appointmentMinutes: 90, dailyMax: 2 },
  { name: "Poodle (Standard)", appointmentMinutes: 90, dailyMax: 2 },
  { name: "Goldendoodle", appointmentMinutes: 90, dailyMax: 2 },
  { name: "Bernese Mountain Dog", appointmentMinutes: 120, dailyMax: 1 },
  { name: "Other / Mixed", appointmentMinutes: 60, dailyMax: 3 },
];

// Default shop timezone. Editable in the admin Settings; .ics times are built
// against whatever is stored on config/shop, never the server's local time.
export const DEFAULT_TIMEZONE = "America/New_York";
